import { useState, useCallback, useEffect, useMemo } from 'react'
import type { FunnelData } from '../../lib/types'
import { EMPTY_FUNNEL_DATA } from '../../lib/types'
import { CATEGORIES } from '../../lib/matching/catalog'

const STORAGE_KEY = 'medilane_funnel_state_v2'

/**
 * Flache, geordnete Liste aller Funnel-Bildschirme.
 * Navigation läuft über einen einzigen questionIndex.
 */
export interface SubQuestion {
  phase: number
  phaseLabel: string
  phaseIcon: string
  questionId: string
  questionLabel: string
}

const q = (
  phase: number,
  phaseLabel: string,
  phaseIcon: string,
  questionId: string,
  questionLabel: string,
): SubQuestion => ({ phase, phaseLabel, phaseIcon, questionId, questionLabel })

export const PHASE_COUNT = 7

export const SUB_QUESTIONS: SubQuestion[] = [
  // Phase 1: Grunddaten
  q(1, 'Grunddaten', '👤', 'name', 'Wie heißen Sie?'),
  q(1, 'Grunddaten', '👤', 'kontakt', 'Wie erreichen wir Sie?'),
  q(1, 'Grunddaten', '👤', 'standort', 'Wo wohnen Sie?'),
  q(1, 'Grunddaten', '👤', 'mobilitaet', 'Wie sind Sie mobil?'),

  // Phase 2: Beruf & Qualifikation
  q(2, 'Beruf & Qualifikation', '📋', 'qualifikation', 'Was ist Ihre Qualifikation?'),
  q(2, 'Beruf & Qualifikation', '📋', 'anerkennung', 'Wie ist Ihr Anerkennungsstatus?'),
  q(2, 'Beruf & Qualifikation', '📋', 'erfahrung', 'Ihre Berufserfahrung'),
  q(2, 'Beruf & Qualifikation', '📋', 'wunsch_bereich', 'In welchen Bereichen möchten Sie arbeiten?'),

  // Phase 3: Arbeitszeit & Rahmen
  q(3, 'Arbeitszeit & Rahmen', '🎯', 'wunsch_stunden', 'Wie viel möchten Sie arbeiten?'),
  q(3, 'Arbeitszeit & Rahmen', '🎯', 'wunsch_zeitpunkt', 'Wann können Sie frühestens wechseln?'),
  q(3, 'Arbeitszeit & Rahmen', '🎯', 'wunsch_gehalt', 'Ihre Gehaltsvorstellung'),
  q(3, 'Arbeitszeit & Rahmen', '🎯', 'wunsch_weg', 'Arbeitsweg & Umzug'),
  q(3, 'Arbeitszeit & Rahmen', '🎯', 'schichten', 'Welche Dienste sind für Sie möglich?'),

  // Phase 4: Wechselgründe
  q(4, 'Wechselgründe', '💭', 'gruende', 'Warum möchten Sie wechseln?'),
  q(4, 'Wechselgründe', '💭', 'gruende_top', 'Ihre 3 wichtigsten Wechselgründe'),

  // Phase 5: Was ist Ihnen wichtig? (ein Bildschirm je Kategorie)
  ...CATEGORIES.map((c) =>
    q(5, 'Was ist Ihnen wichtig?', '⭐', `cat_${c.id}`, c.label),
  ),

  // Phase 6: No-Gos & Wechselbegleitung
  q(6, 'No-Gos & Begleitung', '🛡️', 'nogos', 'Ihre absoluten No-Gos'),
  q(6, 'No-Gos & Begleitung', '🛡️', 'begleitung', 'Wie können wir Sie unterstützen?'),
  q(6, 'No-Gos & Begleitung', '🛡️', 'freitext', 'Möchten Sie uns noch etwas mitteilen?'),

  // Phase 7: Abschluss
  q(7, 'Abschluss', '✅', 'consent', 'Fast geschafft!'),
]

interface StoredState {
  questionIndex: number
  data: FunnelData
  savedAt: string
}

export function useFunnelState() {
  const [questionIndex, setQuestionIndex] = useState(0)
  const [data, setData] = useState<FunnelData>(EMPTY_FUNNEL_DATA)
  const [hasRecovery, setHasRecovery] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const currentQuestion = SUB_QUESTIONS[questionIndex]
  const totalQuestions = SUB_QUESTIONS.length

  const currentPhase = currentQuestion.phase
  const phaseQuestions = useMemo(
    () => SUB_QUESTIONS.filter((x) => x.phase === currentPhase),
    [currentPhase],
  )
  const phaseSubIndex = phaseQuestions.findIndex((x) => x.questionId === currentQuestion.questionId)
  const phaseSubTotal = phaseQuestions.length
  const phaseProgress = (currentPhase / PHASE_COUNT) * 100

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved) {
        const parsed: StoredState = JSON.parse(saved)
        const daysSince = (Date.now() - new Date(parsed.savedAt).getTime()) / 86400000
        if (daysSince < 7 && parsed.data.vorname) setHasRecovery(true)
      }
    } catch {
      /* ignore */
    }
  }, [])

  useEffect(() => {
    if (submitted) return
    try {
      const state: StoredState = { questionIndex, data, savedAt: new Date().toISOString() }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    } catch {
      /* ignore quota */
    }
  }, [questionIndex, data, submitted])

  const recoverState = useCallback(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved) {
        const parsed: StoredState = JSON.parse(saved)
        setData({ ...EMPTY_FUNNEL_DATA, ...parsed.data })
        setQuestionIndex(Math.min(parsed.questionIndex, SUB_QUESTIONS.length - 1))
        setHasRecovery(false)
      }
    } catch {
      /* ignore */
    }
  }, [])

  const dismissRecovery = useCallback(() => {
    setHasRecovery(false)
    localStorage.removeItem(STORAGE_KEY)
  }, [])

  const updateField = useCallback(<K extends keyof FunnelData>(field: K, value: FunnelData[K]) => {
    setData((prev) => ({ ...prev, [field]: value }))
  }, [])

  const updateFields = useCallback((fields: Partial<FunnelData>) => {
    setData((prev) => ({ ...prev, ...fields }))
  }, [])

  const setSlider = useCallback((sliderId: string, value: number) => {
    setData((prev) => ({
      ...prev,
      slider_antworten: { ...prev.slider_antworten, [sliderId]: value },
    }))
  }, [])

  const next = useCallback(() => {
    setQuestionIndex((prev) => Math.min(prev + 1, totalQuestions - 1))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [totalQuestions])

  const prev = useCallback(() => {
    setQuestionIndex((p) => Math.max(p - 1, 0))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const goToPhase = useCallback(
    (phase: number) => {
      const idx = SUB_QUESTIONS.findIndex((x) => x.phase === phase)
      if (idx >= 0 && idx <= questionIndex) {
        setQuestionIndex(idx)
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },
    [questionIndex],
  )

  const markSubmitted = useCallback(() => {
    setSubmitted(true)
    localStorage.removeItem(STORAGE_KEY)
  }, [])

  const reset = useCallback(() => {
    setData(EMPTY_FUNNEL_DATA)
    setQuestionIndex(0)
    setSubmitted(false)
    localStorage.removeItem(STORAGE_KEY)
  }, [])

  return {
    questionIndex,
    currentQuestion,
    currentPhase,
    phaseSubIndex,
    phaseSubTotal,
    phaseProgress,
    totalQuestions,
    data,
    hasRecovery,
    submitted,
    isFirstQuestion: questionIndex === 0,
    isLastQuestion: questionIndex === totalQuestions - 1,
    updateField,
    updateFields,
    setSlider,
    next,
    prev,
    goToPhase,
    recoverState,
    dismissRecovery,
    markSubmitted,
    reset,
  }
}

export type FunnelHook = ReturnType<typeof useFunnelState>
